import { type LoginUserRequest, v1UsersLoginCreate } from '@on3/api';
import { AppleButton } from '@on3/ui-lib/components/Auth/Buttons/Apple';
import { GoogleButton } from '@on3/ui-lib/components/Auth/Buttons/Google';
import { OpenPassButton } from '@on3/ui-lib/components/Auth/Buttons/OpenPass';
import { type FormProps } from '@on3/ui-lib/components/Auth/Forms';
import { ForgotPasswordForm } from '@on3/ui-lib/components/Auth/Forms/ForgotPassword';
import { Brands } from '@on3/ui-lib/components/Brands';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Link } from '@on3/ui-lib/components/Link';
import { externalApi, useAuth, useSite } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { useState } from 'react';
import { object, string } from 'yup';

import styles from './AuthForm.module.scss';

const schema = object({
  username: string().required('Username or email is required'),
  password: string().required('Password is required'),
});

export const LoginForm = ({
  showHeader = true,
  switchForm,
  callback,
}: FormProps) => {
  const { currentSite } = useSite();
  const { formatHref } = useDynamicRoute();
  const { setUserToken } = useAuth();

  const [state, setState] = useState<LoginUserRequest>({
    username: '',
    password: '',
  });
  const [forgotPassword, setForgotPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: LoginUserRequest) => {
    setError(null);

    try {
      const user = await v1UsersLoginCreate(externalApi, values);

      setUserToken(user);
      callback?.(user);
    } catch (e: any) {
      setError('Login failed. Please check your credentials and try again.');
      console.error('Login error:', e);
    }
  };

  if (forgotPassword) {
    return (
      <ForgotPasswordForm
        showHeader={showHeader}
        switchForm={() => setForgotPassword(false)}
      />
    );
  }

  return (
    <Form
      className={styles.block}
      onSubmit={onSubmit}
      schema={schema}
      values={state}
    >
      {({ errors, processing }) => (
        <>
          {showHeader && (
            <header className={styles.header}>
              <h1>Login</h1>
              <Brands className={styles.brands} />
              <p>
                Your account now has access to both <strong>On3</strong> and{' '}
                <strong>Rivals</strong> Networks.
              </p>
            </header>
          )}
          <div className={styles.fields}>
            <div className={styles.sso}>
              <AppleButton />
              <GoogleButton />
              <OpenPassButton />
            </div>
            <p className={styles.or}>
              <span>or</span>
            </p>
            <div className={styles.field}>
              <Input
                autoComplete="email"
                error={errors.username}
                id="email"
                label="Email Address"
                name="email"
                onChange={(e) =>
                  setState({ ...state, username: e.target.value })
                }
                required
              />
            </div>
            <div className={styles.field}>
              <Input
                autoComplete="current-password"
                error={errors.password}
                id="password"
                label="Password"
                name="password"
                onChange={(e) =>
                  setState({ ...state, password: e.target.value })
                }
                required
                type="password"
              />
            </div>
          </div>
          <p className={styles.forgotPassword}>
            <button onClick={() => setForgotPassword(true)} type="button">
              Forgot password?
            </button>
          </p>
          {error && <p className={styles.error}>{error}</p>}
          <button
            className={styles.btnSubmit}
            disabled={processing}
            type="submit"
          >
            {processing ? 'Logging in...' : 'Login'}
          </button>
          <footer className={styles.footer}>
            {switchForm ? (
              <button onClick={() => switchForm('register')} type="button">
                Don&apos;t have an account?
              </button>
            ) : (
              <Link
                href={formatHref({
                  path: '/[site]/register/',
                  params: { site: currentSite.url || '' },
                })}
              >
                Don&apos;t have an account?
              </Link>
            )}
          </footer>
        </>
      )}
    </Form>
  );
};
